import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  DateRange as DateRangeIcon,
  Description as DescriptionIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import empreendimentoService from "../../services/empreendimento";
import { ESTADOS_BRASIL, GRUPO_ATIVIDADE_LABELS, ATIVIDADE_EMPREENDIMENTO_LABELS, empreendimentoStatusLabels, UNIDADE_MEDIDA_AREA_OPTIONS } from "../../utils/constants";
import { formatCPF, formatCNPJ } from "../../utils/formatters";

function DetalhesEmpreendimento() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [empreendimento, setEmpreendimento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    carregarEmpreendimento();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const carregarEmpreendimento = async () => {
    try {
      const data = await empreendimentoService.obter(id);
      setEmpreendimento(data);
    } catch (err) {
      setError("Erro ao carregar dados do empreendimento");
    } finally {
      setLoading(false);
    }
  };

  const getNomeEstado = (sigla) => {
    const estado = ESTADOS_BRASIL.find((estado) => estado.sigla === sigla);
    return estado ? estado.nome : sigla;
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!empreendimento) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Empreendimento não encontrado
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/interessado/empreendimentos")}
          sx={{ mb: { xs: 2, sm: 3 } }}
        >
          Voltar
        </Button>

        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" },
                wordBreak: "break-word",
              }}
            >
              {empreendimento.nome}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Chip
                label={empreendimentoStatusLabels[empreendimento.status] || "Desconhecido"}
                color={
                  empreendimento.status === 0
                    ? "success"
                    : empreendimento.status === 2
                      ? "warning"
                      : "error"
                }
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <DescriptionIcon color="action" sx={{ flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Descrição
                  </Typography>
                  <Typography sx={{ wordBreak: "break-word" }}>
                    {empreendimento.descricao}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <BusinessIcon color="action" />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Informações Gerais
                  </Typography>
                  <Typography>Área: {empreendimento.area} {UNIDADE_MEDIDA_AREA_OPTIONS.find((o) => o.value === (empreendimento.unidadeMedidaArea ?? 0))?.label ?? "ha"}</Typography>
                  <Typography>
                    Responsável: {empreendimento.usuario_nome}
                  </Typography>
                  <Typography>
                    {empreendimento.cadastrarComCpf ? "CPF" : "CNPJ"}:{" "}
                    {empreendimento.cadastrarComCpf
                      ? formatCPF(empreendimento.cnpj)
                      : formatCNPJ(empreendimento.cnpj)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CategoryIcon color="action" sx={{ flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Atividade
                  </Typography>
                  <Typography>
                    Grupo: {GRUPO_ATIVIDADE_LABELS[empreendimento.grupoAtividade] ?? "Não informado"}
                  </Typography>
                  <Typography>
                    Atividade: {ATIVIDADE_EMPREENDIMENTO_LABELS[empreendimento.atividadeEmpreendimento] ?? "Não informado"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <DateRangeIcon color="action" sx={{ flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Datas
                  </Typography>
                  <Typography>
                    Registro:{" "}
                    {new Date(empreendimento.createAt).toLocaleString("pt-BR")}
                  </Typography>
                  <Typography>
                    Última atualização:{" "}
                    {new Date(empreendimento.updateAt).toLocaleString("pt-BR")}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <LocationOnIcon color="action" sx={{ flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Localização
                  </Typography>
                  <Typography>
                    {empreendimento.endereco.rua}
                    {empreendimento.endereco.numero ? `, nº ${empreendimento.endereco.numero}` : " (Sem número)"}
                  </Typography>
                  <Typography>
                    {empreendimento.endereco.municipio} -{" "}
                    {getNomeEstado(empreendimento.endereco.estado)}
                  </Typography>
                  <Typography>CEP: {empreendimento.endereco.cep}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
}

export default DetalhesEmpreendimento;
